import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import MUIDrawer from "@mui/material/Drawer";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useEffect, useState } from "react";
import { matchPath, Link as RRLink, useLocation } from "react-router-dom";
import { fetchFrontendConfig } from "../../api/config/frontend-config";
import { countUnseenReleaseNotes, fetchReleaseNotes } from "../../api/release-notes";
import { fetchSupportUnreadCount } from "../../api/support-tickets";
import ArtemisLogoDark from "../../assets/images/artemis-headhunting_logo-with-text-dark_03.svg?react";
import ArtemisLogoLight from "../../assets/images/artemis-headhunting_logo-with-text-light_03.svg?react";
import { useGlobalState } from "../globalState";
import { getMenuData, MenuItem } from "./menu-structure";

const MenuItemComponent = ({
	item,
	setClosed,
	layer,
}: {
	item: MenuItem;
	setClosed: () => void;
	layer: number;
}) => {
	const location = useLocation();

	const isSubItemSelected = () => {
		if (!item.children) return false;
		return !!item.children.find((child) => {
			if (!child.action) return false;
			return !!matchPath(child.action, location.pathname);
		});
	};

	const [open, setOpen] = useState<boolean>(isSubItemSelected());

	// The Support entry's badge — the reader's tickets carrying unseen writing
	// from the other side — is per-reader async data, so the item fetches its
	// own count rather than threading it through getMenuData.
	const [supportUnread, setSupportUnread] = useState<number>(0);
	useEffect(() => {
		if (item.action !== "/support") return;
		fetchSupportUnreadCount()
			.then(setSupportUnread)
			.catch(() => undefined);
	}, [item.action]);

	const handleClick = () => {
		if (item.children) setOpen(!open);

		if (item.action) {
			setClosed();
		}
	};

	const getButtonComponent = () => {
		if (!item.action) return "div";
		if (item.action.startsWith("http")) return Link;
		else return RRLink;
	};

	const { icon: Icon, title, children } = item;

	if (item.divider) return <Divider />;

	const itemIsSelected = () => {
		if (!item.action) return false;
		return !!matchPath(item.action, location.pathname);
	};

	const menuItemSlug = title?.toLowerCase().replace(/\s+/g, "-");

	return (
		<>
			<ListItemButton
				id={`navbar-menu-item-${menuItemSlug}`}
				data-component={`drawer-menu-${menuItemSlug}`}
				sx={{
					minHeight: layer > 0 ? 32 : "inherit",
					py: (theme) => (layer > 0 ? 0 : theme.spacing(1)),
				}}
				selected={itemIsSelected()}
				key={title}
				component={getButtonComponent()}
				to={item.action}
				href={item.action}
				onClick={handleClick}
			>
				{Icon ? (
					<ListItemIcon data-component={`drawer-menu-${menuItemSlug}-icon`}>
						<Badge
							data-component={`drawer-menu-${menuItemSlug}-badge`}
							badgeContent={
								item.action === "/support"
									? supportUnread
									: (item.badgeCount ?? 0)
							}
							color="primary"
						>
							<Icon />
						</Badge>
					</ListItemIcon>
				) : null}

				<ListItemText
					data-component={`drawer-menu-${menuItemSlug}-text`}
					primary={title}
					slotProps={{
						primary: {
							fontSize: layer > 0 ? 14 : 16,
							fontWeight: "medium",
						},
					}}
				/>

				{children ? open ? <ExpandLess /> : <ExpandMore /> : null}
			</ListItemButton>
			{children ? (
				<Collapse in={open} unmountOnExit>
					<MenuLayer
						layer={layer + 1}
						items={children}
						setClosed={setClosed}
					/>
				</Collapse>
			) : null}
			{layer === 0 ? <Divider /> : null}
		</>
	);
};

const MenuLayer = ({
	items,
	setClosed,
	layer,
}: {
	items: MenuItem[];
	setClosed: () => void;
	layer: number;
}) => {
	return (
		<List
			data-component="drawer-menu-list"
			sx={{
				padding: 0,
			}}
		>
			{items.map((item, idx) => {
				return (
					<MenuItemComponent
						key={`Drawer_Menu_${item.action}-${item.title}-${idx}`}
						layer={layer}
						setClosed={setClosed}
						item={item}
					/>
				);
			})}
		</List>
	);
};

export const Drawer = ({
	open,
	setClosed,
}: {
	open: boolean;
	setClosed: () => void;
}) => {
	const theme = useTheme();
	const { currentUser } = useGlobalState();
	// Fetched once: the corpus is small, one call answers both "how many are
	// unseen" and, on the page itself, what to render. Staff-only, because
	// that is who the reading surface is open to.
	const [unseenReleaseNotes, setUnseenReleaseNotes] = useState<number>(0);
	// Import-from-prod exists only on non-prod instances; prod importing from
	// itself is nonsense, so prod never shows the entry.
	const [prodImportEnabled, setProdImportEnabled] = useState<boolean>(false);

	useEffect(() => {
		if (!currentUser?.is_staff) return;
		fetchReleaseNotes()
			.then((payload) => setUnseenReleaseNotes(countUnseenReleaseNotes(payload)))
			.catch(() => undefined);
		fetchFrontendConfig()
			.then((config) => setProdImportEnabled(Boolean(config.prodImportEnabled)))
			.catch(() => undefined);
	}, [currentUser?.is_staff]);

	return (
		<MUIDrawer
			data-component="nav-drawer"
			anchor="left"
			open={open}
			transitionDuration={50}
			onClose={() => setClosed()}
		>
			<Box
				sx={{
					width: 250,
					flexGrow: 1,
					flexDirection: "column",
					display: "flex",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						flexGrow: 1,
					}}
				>
					<Box
						sx={{
							flexGrow: 1,
						}}
					>
						<Box
							data-component="drawer-logo-container"
							sx={{
								display: "flex",
								justifyContent: "center",
								padding: theme.spacing(2),
							}}
						>
							{theme.palette.mode === "dark" ? (
								<ArtemisLogoLight
									data-component="drawer-logo"
									style={{ width: 150, height: "auto" }}
								/>
							) : (
								<ArtemisLogoDark
									data-component="drawer-logo"
									style={{ width: 150, height: "auto" }}
								/>
							)}
						</Box>
						<MenuLayer
							layer={0}
							setClosed={setClosed}
							items={getMenuData(
								currentUser,
								unseenReleaseNotes,
								prodImportEnabled
							)}
						/>
					</Box>
				</Box>
			</Box>
		</MUIDrawer>
	);
};
